import React, { useState } from 'react';
import { Search, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

interface LayoffRecord {
  id: string;
  company: string;
  state: string;
  layoff_date: string;
  workers_affected: number;
  source: string;
  is_closure: boolean;
  notes?: string;
}

type SortField = 'company' | 'state' | 'layoff_date' | 'workers_affected';
type SortDirection = 'asc' | 'desc';

export function LayoffTrackerPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [records, setRecords] = useState<LayoffRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [sortField, setSortField] = useState<SortField>('layoff_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const loadRecords = async () => {
    try {
      const { data, error } = await supabase
        .from('layoff_records')
        .select(`
          id,
          company,
          state,
          workers_affected,
          layoff_date,
          is_closure,
          notes,
          created_at
        `)
        .order('layoff_date', { ascending: false });

      if (error) throw error;
      setRecords(data || []);
    } catch (error) {
      console.error('Error loading records:', error);
      toast.error('Failed to load records');
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    loadRecords();
  }, []);

  const filteredRecords = records.filter(record =>
    record.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
    record.state.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedRecords = [...filteredRecords].sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    
    switch (sortField) {
      case 'company':
        return direction * a.company.localeCompare(b.company);
      case 'state':
        return direction * a.state.localeCompare(b.state);
      case 'layoff_date':
        return direction * (new Date(a.layoff_date).getTime() - new Date(b.layoff_date).getTime());
      case 'workers_affected':
        return direction * (a.workers_affected - b.workers_affected);
      default:
        return 0;
    }
  });

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      e.target.value = '';
      return;
    }

    setIsImporting(true);
    try {
      const text = await file.text();
      const lines = text.split('\n')
        .map(line => line.trim())
        .filter(Boolean);
      
      if (lines.length === 0) {
        throw new Error('CSV file is empty');
      }

      // Parse CSV headers, handling quoted values
      const headers = lines[0]
        .split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
        .map(h => h.trim().toLowerCase().replace(/^"|"$/g, ''));

      // Required columns
      const requiredColumns = ['company', 'state', 'employees_affected', 'date'];
      const missingColumns = requiredColumns.filter(col => {
        const matchingHeader = headers.find(h => {
          if (col === 'employees_affected') {
            return h.includes('employees') || h.includes('workers');
          }
          return h.includes(col);
        });
        return !matchingHeader;
      });

      if (missingColumns.length > 0) {
        throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
      }

      // Process records in batches
      const batchSize = 100;
      const allRecords = [];
      
      for (let i = 1; i < lines.length; i += batchSize) {
        const batch = lines.slice(i, i + batchSize)
        .filter(line => line.trim())
        .map(line => {
          // Split CSV line handling quoted values
          const values = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
            .map(v => v.trim().replace(/^"|"$/g, ''));

          const record: any = {};
          
          headers.forEach((header, index) => {
            const value = values[index] || '';
            
            if (header.includes('company')) {
              record.company = value;
            } else if (header === 'state' || header.includes('location')) {
              // Extract state from location if needed
              record.state = value.split(',').pop()?.trim() || value;
            } else if (header.includes('employees') || header.includes('workers')) {
              // Handle empty or non-numeric values
              if (!value) return;
              const numStr = value.replace(/[^0-9,]/g, '').replace(',', '');
              if (numStr) {
                const num = parseInt(numStr, 10);
                if (!isNaN(num) && num > 0) {
                  record.workers_affected = num;
                }
              }
            } else if (header === 'date' || header.includes('date')) {
              try {
                if (!value) return;
                
                let date;
                if (value.includes('-')) {
                  // Handle date ranges by taking the first date
                  const [firstDate] = value.split('-');
                  date = new Date(firstDate.trim());
                } else if (value.includes('/')) {
                  // Handle MM/DD/YYYY format
                  const [month, day, year] = value.split('/');
                  if (year && month && day) {
                    date = new Date(
                      parseInt(year.length === 2 ? '20' + year : year),
                      parseInt(month) - 1,
                      parseInt(day)
                    );
                  }
                } else {
                  date = new Date(value);
                }
                
                if (date && !isNaN(date.getTime())) {
                  record.layoff_date = date.toISOString().slice(0, 10);
                }
              } catch (error) {
                console.error('Error parsing date:', error);
              }
            } else if (header.includes('closure') || header.includes('layoff')) {
              record.is_closure = value.toLowerCase().includes('closure');
            }
          });

          return record;
        })
        .filter(record => 
          record.company &&
          record.state &&
          record.layoff_date &&
          record.workers_affected > 0
        );

        allRecords.push(...batch);
        
        // Show progress
        toast.success(`Processed ${Math.min(i + batchSize, lines.length - 1)} of ${lines.length - 1} records`, {
          id: 'import-progress',
          duration: 1000
        });
      }

      if (allRecords.length === 0) {
        throw new Error('No valid records found in CSV');
      }

      // Insert records in batches
      let insertedCount = 0;
      for (let i = 0; i < allRecords.length; i += batchSize) {
        const batch = allRecords.slice(i, i + batchSize);
        const { error } = await supabase
          .from('layoff_records')
          .insert(batch);
        
        if (error) throw error;
        insertedCount += batch.length;
      }

      toast.success(`Successfully imported ${insertedCount} records`);
      await loadRecords();
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to import CSV');
    } finally {
      setIsImporting(false);
      e.target.value = ''; // Reset file input
    }
  };

  return (
    <div className="min-h-screen bg-background-dark py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center text-accent-green hover:opacity-80"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Admin Dashboard
          </Link>
        </div>

        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-black">Layoff Tracker</h1>
          <div>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              id="layoff-csv-upload"
            />
            <label
              htmlFor="layoff-csv-upload"
              className={`inline-flex items-center px-4 py-2 bg-[#373737] text-white rounded-xl hover:opacity-90 transition-colors cursor-pointer ${
                isImporting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isImporting ? 'Importing...' : 'Import CSV'}
            </label>
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by company or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center p-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('company')}
                    >
                      <span className="inline-flex items-center">
                        Company
                        <span className="ml-1">{getSortIcon('company')}</span>
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('state')}
                    >
                      <span className="inline-flex items-center">
                        State
                        <span className="ml-1">{getSortIcon('state')}</span>
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('layoff_date')}
                    >
                      <span className="inline-flex items-center">
                        Layoff Date
                        <span className="ml-1">{getSortIcon('layoff_date')}</span>
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('workers_affected')}
                    >
                      <span className="inline-flex items-center">
                        Workers Affected
                        <span className="ml-1">{getSortIcon('workers_affected')}</span>
                      </span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedRecords.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {record.company}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.state}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(record.layoff_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.workers_affected.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.is_closure ? 'Closure' : 'Layoff'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}